import { EXIT_INTENT_EVENTS } from '@/constants/amplitude.constants';
import Amplitude from '@/shared/services/amplitude.service';
import { getCurrentURL } from '@/shared/utils/commons';
import { getUTMFieldsFromLocalStorage } from '@/shared/utils/utm.util';
import { useCallback, useEffect, useState } from 'react';

export const useExitIntent = () => {
    const [shouldTrigger, setShouldTrigger] = useState(false);
    const [variant, setVariant] = useState<'A' | 'B' | null>(null);
    const [hasTriggered, setHasTriggered] = useState(false);
    let lastScrollTop = 0;
    let hasScrolledPast200 = false;

    useEffect(() => {
        const handleDemoButtonClick = () => {
            sessionStorage.setItem('BOOK_DEMO_TAPPED', 'true');
            sessionStorage.setItem('hasClickedDemo', 'true');
        };

        const demoButtons = document.querySelectorAll('button, a');
        demoButtons.forEach(button => {
            if (
                button.textContent?.toLowerCase().includes('schedule a demo') ||
                button.textContent?.toLowerCase().includes('book a demo') ||
                button.textContent?.toLowerCase().includes('join now')
            ) {
                button.addEventListener('click', handleDemoButtonClick);
            }
        })

        const storedVariant = sessionStorage.getItem('exitIntentVariant');
        if (storedVariant) {
            setVariant(storedVariant as 'A' | 'B');
        } else {
            const newVariant = Math.random() < 0.5 ? 'A' : 'B';
            sessionStorage.setItem('exitIntentVariant', newVariant);
            setVariant(newVariant);
        }

        // Cleanup
        return () => {
            demoButtons.forEach(button => {
                button.removeEventListener('click', handleDemoButtonClick);
            });
        };
    }, []);

    const hasInteracted = useCallback(() => {
        const currentPath = window.location.pathname;
        const isThinkificPage = currentPath.includes('/us/page/thinkific-alternative/');
        const hasBookedDemo = 
            sessionStorage.getItem('BOOK_DEMO_TAPPED') === 'true' || 
            sessionStorage.getItem('hasClickedDemo') === 'true' 

        if (isThinkificPage) {
            return sessionStorage.getItem('exitIntentFormSubmitted') === 'true' || 
                   hasBookedDemo;
        }

        return sessionStorage.getItem('exitIntentFormSubmitted') === 'true' || 
               hasBookedDemo;
    }, []);

    const handleTrigger = useCallback((immediate: boolean = false) => {
        if (!hasInteracted() && variant && !hasTriggered) {
            setHasTriggered(true);
            
            const showPopup = () => {
                if (!hasInteracted()) {
                    setShouldTrigger(true);
                    const amplitude = new Amplitude();
                    const utmParams = getUTMFieldsFromLocalStorage();
                    amplitude.sendEvent({
                        type: EXIT_INTENT_EVENTS.POPUP_SHOWN.LABEL,
                        properties: {
                            current_url: getCurrentURL(),
                            ...utmParams,
                            variant,
                            delay: immediate ? 0 : 3,
                            timestamp: new Date().toISOString()
                        }
                    });
                }
            };

            if (immediate) {
                showPopup();
            } else if (variant === 'A') {
                setTimeout(showPopup, 3000);
            }
        }
    }, [hasInteracted, variant, hasTriggered]);

    
    const handleScroll = useCallback(() => {
        if (!variant || variant === 'B') return;
        
        const st = window.scrollY || document.documentElement.scrollTop;
       
        if (st > 200) {
            hasScrolledPast200 = true;
        }
        
        if (hasScrolledPast200 && st < lastScrollTop) {
            handleTrigger(false);
            hasScrolledPast200 = false; 
        }
        lastScrollTop = st;
    }, [handleTrigger, variant]);

    const handleMouseMovement = useCallback((e: MouseEvent) => {
        if (hasInteracted() || !variant) return;
        
        const browserWidth = window.innerWidth;
        
        if (e.clientX <= 140 && e.clientY <= 20) {
            handleTrigger(true);
            return;
        }
    
        
        if (e.clientX >= browserWidth - 140 && e.clientY <= 20) {
            handleTrigger(true);
            return;
        }
    }, [handleTrigger, hasInteracted, variant]);

    const handleVisibilityChange = useCallback(() => {
        if (document.hidden && !hasInteracted() && variant) {
            handleTrigger(true);
        }
    }, [handleTrigger, hasInteracted, variant]);

    const handleWindowBlur = useCallback(() => {
        if (!hasInteracted() && variant) {
            handleTrigger(true);
        }
    }, [handleTrigger, hasInteracted, variant]);

    useEffect(() => {
        if (hasInteracted()) {
            setShouldTrigger(false);
            setHasTriggered(false);
            return;
        }

       
        document.addEventListener('mousemove', handleMouseMovement);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleWindowBlur);
        window.addEventListener('scroll', handleScroll);

        return () => {
            document.removeEventListener('mousemove', handleMouseMovement);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('blur', handleWindowBlur);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [
        handleMouseMovement, 
        handleVisibilityChange, 
        handleWindowBlur, 
        handleScroll, 
        hasInteracted
    ]);

    return shouldTrigger;
};